import type { Client, Task, TriageItem } from '../types/models';

/**
 * Seed data ported from the Studio Board design output. Used when
 * `VITE_USE_FIRESTORE=false`, and as the payload for the one-time Firestore
 * seeding script (see README).
 */

export const seedClients: Client[] = [
  {
    id: 'c1',
    name: 'Northwind Coffee',
    initials: 'NC',
    avatarBg: 'linear-gradient(135deg,#24b1ba,#7ccaaa)',
    order: 0,
    projects: [
      { id: 'p1', name: 'Website Redesign' },
      { id: 'p2', name: 'Brand Refresh' },
    ],
  },
  {
    id: 'c2',
    name: 'Harbor Dental',
    initials: 'HD',
    avatarBg: 'linear-gradient(135deg,#4b8bbf,#7ccaaa)',
    order: 1,
    projects: [{ id: 'p3', name: 'Booking Flow' }],
  },
  {
    id: 'c3',
    name: 'Vela Studios',
    initials: 'VS',
    avatarBg: 'linear-gradient(135deg,#7ccaaa,#24b1ba)',
    order: 2,
    projects: [{ id: 'p4', name: 'Portfolio Site' }],
  },
  {
    id: 'c4',
    name: 'Ridgeline Outfitters',
    initials: 'RO',
    avatarBg: 'linear-gradient(135deg,#24b1ba,#4b8bbf)',
    order: 3,
    projects: [{ id: 'p5', name: 'Shopify Migration' }],
  },
  {
    id: 'c5',
    name: 'Cortez & Fox Law',
    initials: 'CF',
    avatarBg: 'linear-gradient(135deg,#6f7cbf,#7ccaaa)',
    order: 4,
    projects: [],
  },
  {
    id: 'c6',
    name: 'Bloom Botanicals',
    initials: 'BB',
    avatarBg: 'linear-gradient(135deg,#7ccaaa,#4b8bbf)',
    order: 5,
    projects: [{ id: 'p6', name: 'Spring Email Campaign' }],
  },
];

export const seedTasks: Task[] = [
  { id: 't1', clientId: 'c1', title: 'Finalize homepage hero copy', due: 'today', dueLabel: 'Today', projectId: 'p1', est: '1h', status: 'doing', notes: 'They want the “sourced direct” line up top. Waiting on the new roaster photo.', links: '2 links', ai: false, done: false },
  { id: 't2', clientId: 'c1', title: 'Build the menu page grid component', due: 'soon', dueLabel: 'Fri', projectId: 'p1', est: '3h', status: 'todo', notes: 'Responsive 2-up → 4-up. Reuse the card from the portfolio template.', links: '', ai: true, done: false },
  { id: 't3', clientId: 'c1', title: 'Draft brand voice one-pager', due: 'soon', dueLabel: 'Fri', projectId: 'p2', est: '2h', status: 'todo', notes: '', links: '', ai: true, done: false },
  { id: 't4', clientId: 'c1', title: 'Export logo variations (SVG + PNG)', due: 'later', dueLabel: 'Next wk', projectId: 'p2', est: '45m', status: 'todo', notes: '', links: '', ai: false, done: false },
  { id: 't5', clientId: 'c1', title: 'Send invoice #0142', due: 'overdue', dueLabel: '2d late', projectId: '', est: '15m', status: 'todo', notes: 'March retainer.', links: '', ai: false, done: false },
  { id: 't6', clientId: 'c1', title: 'Set up staging deploy', due: 'later', dueLabel: 'Next wk', projectId: 'p1', est: '1h', status: 'todo', notes: '', links: '1 link', ai: true, done: true },

  { id: 't7', clientId: 'c2', title: 'QA the appointment confirmation email', due: 'today', dueLabel: 'Today', projectId: 'p3', est: '30m', status: 'doing', notes: 'Broken on Outlook — table widths.', links: '', ai: false, done: false },
  { id: 't8', clientId: 'c2', title: 'Wire up Calendly embed', due: 'soon', dueLabel: 'Thu', projectId: 'p3', est: '1h', status: 'todo', notes: '', links: '1 link', ai: true, done: false },
  { id: 't9', clientId: 'c2', title: 'Compress hero background image', due: 'soon', dueLabel: 'Thu', projectId: 'p3', est: '20m', status: 'todo', notes: '', links: '', ai: true, done: false },
  { id: 't10', clientId: 'c2', title: 'Add insurance FAQ section', due: 'later', dueLabel: 'Next wk', projectId: 'p3', est: '1.5h', status: 'todo', notes: 'Copy coming from Dr. Reyes.', links: '', ai: false, done: false },

  { id: 't11', clientId: 'c3', title: 'Refactor gallery to CSS grid', due: 'overdue', dueLabel: '1d late', projectId: 'p4', est: '2h', status: 'doing', notes: 'Masonry is janky on Safari. Move to grid + aspect-ratio.', links: '', ai: true, done: false },
  { id: 't12', clientId: 'c3', title: 'Write alt text for 40 images', due: 'soon', dueLabel: 'Mon', projectId: 'p4', est: '1h', status: 'todo', notes: '', links: '', ai: true, done: false },
  { id: 't13', clientId: 'c3', title: 'Client review call', due: 'today', dueLabel: 'Today 3pm', projectId: '', est: '45m', status: 'todo', notes: '', links: '1 link', ai: false, done: false },

  { id: 't14', clientId: 'c4', title: 'Migrate 120 product SKUs', due: 'soon', dueLabel: 'Fri', projectId: 'p5', est: '4h', status: 'doing', notes: 'CSV import + manual variant cleanup. Good agent candidate.', links: '1 link', ai: true, done: false },
  { id: 't15', clientId: 'c4', title: 'Set up 301 redirect map', due: 'soon', dueLabel: 'Fri', projectId: 'p5', est: '2h', status: 'todo', notes: '', links: '', ai: true, done: false },
  { id: 't16', clientId: 'c4', title: 'Configure shipping zones', due: 'later', dueLabel: 'Next wk', projectId: 'p5', est: '1h', status: 'todo', notes: '', links: '', ai: false, done: false },
  { id: 't17', clientId: 'c4', title: 'Theme color + font pass', due: 'later', dueLabel: 'Next wk', projectId: 'p5', est: '2h', status: 'todo', notes: '', links: '', ai: false, done: false },
  { id: 't18', clientId: 'c4', title: 'Approve final invoice split', due: 'today', dueLabel: 'Today', projectId: '', est: '10m', status: 'todo', notes: '', links: '', ai: false, done: false },

  { id: 't19', clientId: 'c5', title: 'Fix contact form spam (add honeypot)', due: 'today', dueLabel: 'Today', projectId: '', est: '45m', status: 'doing', notes: 'Getting ~30 spam entries/day.', links: '', ai: true, done: false },
  { id: 't20', clientId: 'c5', title: 'Update attorney bio photos', due: 'later', dueLabel: 'Next wk', projectId: '', est: '30m', status: 'todo', notes: '', links: '1 link', ai: false, done: false },
  { id: 't21', clientId: 'c5', title: 'Monthly maintenance + backup', due: 'soon', dueLabel: 'Mon', projectId: '', est: '1h', status: 'todo', notes: 'Recurring retainer task.', links: '', ai: false, done: false },

  { id: 't22', clientId: 'c6', title: 'Design 3 email templates', due: 'soon', dueLabel: 'Wed', projectId: 'p6', est: '3h', status: 'doing', notes: 'Launch, mid-sale, last-chance.', links: '', ai: false, done: false },
  { id: 't23', clientId: 'c6', title: 'Segment the list in Klaviyo', due: 'soon', dueLabel: 'Wed', projectId: 'p6', est: '1h', status: 'todo', notes: '', links: '', ai: true, done: false },
  { id: 't24', clientId: 'c6', title: 'Write subject line variants', due: 'later', dueLabel: 'Next wk', projectId: 'p6', est: '45m', status: 'todo', notes: '', links: '', ai: true, done: false },
];

export const seedTriage: TriageItem[] = [
  { id: 'tr1', title: '“Can we swap the banner before Friday?” — from Dana', source: 'Slack', sourceColor: '#8e44e0', age: '12m ago', resolved: false },
  { id: 'tr2', title: 'New logo files attached, please add to footer', source: 'Email', sourceColor: '#24b1ba', age: '1h ago', resolved: false },
  { id: 'tr3', title: 'Site is down?? call me', source: 'Text', sourceColor: '#42c76a', age: '2h ago', resolved: false },
  { id: 'tr4', title: 'Move launch date to the 28th', source: 'Monday.com', sourceColor: '#ff3d57', age: '3h ago', resolved: false },
  { id: 'tr5', title: 'Add team page — 6 headshots + bios', source: 'Teams', sourceColor: '#4b53bc', age: 'Yesterday', resolved: false },
  { id: 'tr6', title: 'Meeting notes: wants a blog section eventually', source: 'Notion', sourceColor: '#a0a3b1', age: 'Yesterday', resolved: false },
];
